import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";

const navItems = [
  { href: "/services", label: "Services" },
  { href: "/#plateformes", label: "Plateformes" },
  { href: "/#produits", label: "Produits" },
  { href: "/formations", label: "Formations" },
  { href: "/#contact", label: "Contact" },
];

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Menu mobile : page derrière immobile, focus gardé dedans, Échap ferme
  // et rend le focus au bouton qui l'a ouvert.
  useEffect(() => {
    if (!isOpen) return undefined;

    const opener = openerRef.current;
    const focusables = () =>
      menuRef.current?.querySelectorAll("a[href], button:not([disabled])") ??
      [];

    document.body.style.overflow = "hidden";
    focusables()[0]?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
      opener?.focus({ preventScroll: true });
    };
  }, [isOpen]);

  const close = () => setIsOpen(false);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-[1000] border-b border-border bg-white ${
          isScrolled ? "shadow-[0_1px_8px_rgba(8,61,68,0.08)]" : ""
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center gap-10 px-6 py-4 sm:px-10 lg:px-14">
          <Link to="/" aria-label="Geodonnée, accueil">
            <Logo />
          </Link>

          <nav
            aria-label="Navigation principale"
            className="hidden flex-1 items-center gap-8 lg:flex"
          >
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="text-vt-nav font-bold text-t2 transition-colors hover:text-brand"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <Link
            to="/#contact"
            className="btn-accent btn-sm ml-auto hidden lg:inline-flex"
          >
            Consultation gratuite
            <ArrowRight
              className="h-[1.125rem] w-[1.125rem]"
              strokeWidth={2.4}
              aria-hidden="true"
            />
          </Link>

          <button
            ref={openerRef}
            type="button"
            onClick={() => setIsOpen(true)}
            aria-label="Ouvrir le menu"
            title="Ouvrir le menu"
            aria-expanded={isOpen}
            aria-controls="menu-mobile"
            className="ml-auto rounded-xl p-2 text-brand lg:hidden"
          >
            <Menu className="h-7 w-7" strokeWidth={2.2} aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Menu mobile plein écran */}
      {isOpen && (
        <div
          ref={menuRef}
          id="menu-mobile"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          className="on-dark fixed inset-0 z-[1001] flex flex-col overflow-y-auto overscroll-contain bg-brand px-6 py-5 lg:hidden"
        >
          <div className="flex items-center justify-between">
            <Logo clair />
            <button
              type="button"
              onClick={close}
              aria-label="Fermer le menu"
              title="Fermer le menu"
              className="rounded-xl p-2 text-white"
            >
              <X className="h-7 w-7" strokeWidth={2.2} aria-hidden="true" />
            </button>
          </div>

          <nav aria-label="Navigation principale" className="mt-8 flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={close}
                className="border-b border-white/20 py-5 text-left font-display text-vt-h3 font-bold text-white"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <Link to="/#contact" onClick={close} className="btn-accent mt-auto w-full">
            Consultation gratuite
            <ArrowRight className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
          </Link>
        </div>
      )}
    </>
  );
};
